/** Development menu: theme (host:theme arrives with PRISM in M5), canvas state, overlay settings. */
import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n.ts';
import { getTheme, setTheme } from '../theme.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { NumberField, SelectField, Toggle } from '../ui/controls.tsx';
import { centreCut } from '../model/snapping.ts';

export function DevMenu({ store, onClose }: { store: EditorStore; onClose: () => void }) {
  const ui = useEditor(store, (s) => s.ui);
  const canvas = useEditor(store, (s) => sceneView(s).canvas);
  const [theme, setThemeState] = useState(getTheme());
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as Element).closest('.dev-anchor')) onClose();
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [onClose]);
  return (
    <div className="popover dev-menu" ref={ref} role="dialog" aria-label={t('dev.title')} data-testid="dev-menu-panel">
      <h3>{t('dev.title')}</h3>
      <SelectField
        label={t('dev.theme')}
        value={theme}
        testId="theme-select"
        options={[
          { value: 'dark', label: t('dev.themeDark') },
          { value: 'light', label: t('dev.themeLight') },
        ]}
        onChange={(v) => {
          setTheme(v);
          setThemeState(v);
        }}
      />
      <SelectField
        label={t('dev.renderState')}
        value={ui.renderState}
        options={[
          { value: 'hold', label: t('dev.stateHold') },
          { value: 'design', label: t('dev.stateDesign') },
        ]}
        onChange={(v) => store.setUi({ renderState: v })}
      />
      <Toggle label={t('view.safe')} checked={ui.showSafe} onChange={(v) => store.setUi({ showSafe: v })} />
      <div className="row two">
        <NumberField label={t('dev.actionSafe')} short={t('dev.actionSafe.short')} value={ui.actionSafe} min={0} max={20} step={0.5} suffix="%" onChange={(v) => store.setUi({ actionSafe: v })} />
        <NumberField label={t('dev.graphicsSafe')} short={t('dev.graphicsSafe.short')} value={ui.graphicsSafe} min={0} max={20} step={0.5} suffix="%" onChange={(v) => store.setUi({ graphicsSafe: v })} />
      </div>
      {centreCut(canvas.width, canvas.height) && <Toggle label={t('view.cut')} checked={ui.showCut} onChange={(v) => store.setUi({ showCut: v })} />}
      <Toggle label={t('view.rulers')} checked={ui.showRulers} onChange={(v) => store.setUi({ showRulers: v })} />
      <Toggle label={t('view.guides')} checked={ui.showGuides} onChange={(v) => store.setUi({ showGuides: v })} />
      <Toggle label={t('view.snapping')} checked={ui.snapping} onChange={(v) => store.setUi({ snapping: v })} />
      <Toggle label={t('view.reference')} checked={ui.showReference} onChange={(v) => store.setUi({ showReference: v })} />
      {(ui.guides.x.length > 0 || ui.guides.y.length > 0) && (
        <button type="button" className="btn small" onClick={() => store.setUi({ guides: { x: [], y: [] } })}>
          {t('dev.clearGuides')}
        </button>
      )}
    </div>
  );
}
